import React, { useState } from "react";
import YhInput from "../../../../../../ui/YhInput";
import YhButton from "../../../../../../ui/YhButton";
import { addLocationMeta } from "./projectLocationService";
import { openSuccessNotification } from "../../../../../../helpers/methods";
import { ImageUpLoad } from "../../../../../Team/components/stepper/EmployeeDocuments/EmployeeDocumentServices";

const WebsiteToggle = ({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
}) => (
  <label className="flex items-center justify-between gap-3 rounded-xl border border-[#EEF0F5] bg-white px-3 py-2">
    <span className="text-sm font-medium text-[#374151]">{label}</span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
        checked ? "bg-[#A02448]" : "bg-[#D1D5DB]"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
          checked ? "left-5" : "left-0.5"
        }`}
      />
    </button>
  </label>
);

const ProjectLocationCreate = ({ state, setStateData, closeDrawer }: any) => {
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCityKey, setUploadingCityKey] = useState<string | null>(null);
  const data = state.countryDrawerData || {};
  const isEdit = !!data?._id;

  const uploadImage = async (file: File) => {
    const form = new FormData();
    form.append("file", file);
    const uploadedUrl = await ImageUpLoad(form);
    return uploadedUrl.imageUrl as string;
  };

  const handleCountryFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const imageUrl = await uploadImage(file);
      setStateData({
        countryDrawerData: {
          ...data,
          icon: imageUrl,
        },
      });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleCityFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    stateIdx: number,
    cityIdx: number
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const key = `${stateIdx}-${cityIdx}`;
    try {
      setUploadingCityKey(key);
      const imageUrl = await uploadImage(file);
      handleCityChange(stateIdx, cityIdx, "icon", imageUrl);
    } finally {
      setUploadingCityKey(null);
      e.target.value = "";
    }
  };

  const handleCountryChange = (key: string, value: any) => {
    setStateData({
      countryDrawerData: {
        ...data,
        [key]: value,
      },
    });
  };

  const handleStateChange = (index: number, key: string, value: string) => {
    const updatedStates = [...(data?.states || [])];
    updatedStates[index] = { ...updatedStates[index], [key]: value };
    setStateData({
      countryDrawerData: { ...data, states: updatedStates },
    });
  };

  const handleCityChange = (
    stateIdx: number,
    cityIdx: number,
    key: string,
    value: any
  ) => {
    const states = [...(data?.states || [])];
    const cities = [...(states[stateIdx].cities || [])];
    cities[cityIdx] = { ...cities[cityIdx], [key]: value };
    states[stateIdx] = { ...states[stateIdx], cities };
    setStateData({ countryDrawerData: { ...data, states } });
  };

  const handleZoneChange = (
    stateIdx: number,
    cityIdx: number,
    zoneIdx: number,
    key: string,
    value: any
  ) => {
    const states = [...(data?.states || [])];
    const cities = [...(states[stateIdx].cities || [])];
    const zones = [...(cities[cityIdx].zones || [])];
    zones[zoneIdx] = { ...zones[zoneIdx], [key]: value };
    cities[cityIdx] = { ...cities[cityIdx], zones };
    states[stateIdx] = { ...states[stateIdx], cities };
    setStateData({ countryDrawerData: { ...data, states } });
  };

  const addState = () => {
    setStateData({
      countryDrawerData: {
        ...data,
        states: [...(data?.states || []), { name: "", cities: [] }],
      },
    });
  };

  const removeState = (index: number) => {
    const states = [...(data?.states || [])];
    states.splice(index, 1);
    setStateData({ countryDrawerData: { ...data, states } });
  };

  const addCity = (stateIdx: number) => {
    const states = [...(data?.states || [])];
    const cities = [...(states[stateIdx].cities || [])];
    cities.push({ name: "", zones: [{ name: "" }], enabled: false });
    states[stateIdx] = { ...states[stateIdx], cities };
    setStateData({ countryDrawerData: { ...data, states } });
  };

  const removeCity = (stateIdx: number, cityIdx: number) => {
    const states = [...(data?.states || [])];
    const cities = [...(states[stateIdx].cities || [])];
    cities.splice(cityIdx, 1);
    states[stateIdx] = { ...states[stateIdx], cities };
    setStateData({ countryDrawerData: { ...data, states } });
  };

  const addLocation = (stateIdx: number, cityIdx: number) => {
    const states = [...(data?.states || [])];
    const cities = [...(states[stateIdx].cities || [])];
    const zones = [...(cities[cityIdx].zones || [])];
    zones.push({ name: "" });
    cities[cityIdx] = { ...cities[cityIdx], zones };
    states[stateIdx] = { ...states[stateIdx], cities };
    setStateData({ countryDrawerData: { ...data, states } });
  };

  const removeLocation = (
    stateIdx: number,
    cityIdx: number,
    locIdx: number
  ) => {
    const states = [...(data?.states || [])];
    const cities = [...(states[stateIdx].cities || [])];
    const zones = [...(cities[cityIdx].zones || [])];
    zones.splice(locIdx, 1);
    cities[cityIdx] = { ...cities[cityIdx], zones };
    states[stateIdx] = { ...states[stateIdx], cities };
    setStateData({ countryDrawerData: { ...data, states } });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const response = await addLocationMeta(data);
      if (response) {
        closeDrawer();
        openSuccessNotification(response?.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4"
      onClick={closeDrawer}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[#EEF0F5] px-5 py-4">
          <h2 className="text-lg font-bold text-[#111827]">
            {isEdit ? "Edit Location Meta" : "Create Location Meta"}
          </h2>
          <button
            type="button"
            onClick={closeDrawer}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <YhInput
              label="Country Name"
              placeholder="Country Name"
              value={data?.country_name || ""}
              onChange={(e) =>
                handleCountryChange("country_name", e.target.value)
              }
            />
            <YhInput
              label="Country Short Code"
              placeholder="Short Code"
              value={data?.short_code || ""}
              onChange={(e) =>
                handleCountryChange("short_code", e.target.value)
              }
            />
            <YhInput
              label="Country Phone Code"
              placeholder="Country Code"
              value={data?.country_code || ""}
              onChange={(e) =>
                handleCountryChange("country_code", e.target.value)
              }
            />
            <YhInput
              label="Currency"
              placeholder="Currency"
              value={data?.currency || ""}
              onChange={(e) => handleCountryChange("currency", e.target.value)}
            />
          </div>

          <div className="mt-4">
            <WebsiteToggle
              checked={!!data?.enabled}
              onChange={(value) => handleCountryChange("enabled", value)}
              label="Show country on website header"
            />
            <p className="mt-1 text-xs text-[#6B7280]">
              Off = country is hidden on yhataw.com. Pulse admin still keeps this record.
            </p>
          </div>

          <div className="mt-4">
            <label className="yh-label">Country Icon</label>
            <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-[#D1D5DB] bg-[#F9FAFB] px-4 py-6 text-center hover:border-[#A02448] hover:bg-[#F5F6FF]">
              <span className="text-sm text-[#6B7280]">
                {uploading
                  ? "Uploading…"
                  : "Drag and drop or click to upload"}
              </span>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleCountryFileChange}
              />
            </label>
            {data?.icon ? (
              <div className="relative mt-3 inline-block h-20 w-20 overflow-hidden rounded-lg border border-[#EEF0F5]">
                <img
                  src={data.icon}
                  alt="Country"
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() =>
                    setStateData({
                      countryDrawerData: { ...data, icon: "" },
                    })
                  }
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white"
                >
                  ✕
                </button>
              </div>
            ) : null}
          </div>

          <div className="mt-6 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#111827]">States</h3>
            <YhButton variant="secondary" size="sm" onClick={addState}>
              + Add State
            </YhButton>
          </div>

          {(data?.states || []).map((st: any, stateIdx: number) => (
            <div
              key={stateIdx}
              className="mt-4 rounded-xl border border-[#EEF0F5] bg-[#FAFBFC] p-4"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]">
                <YhInput
                  label="State Name"
                  placeholder="State Name"
                  value={st.name || ""}
                  onChange={(e) =>
                    handleStateChange(stateIdx, "name", e.target.value)
                  }
                />
                <YhInput
                  label="State Short Code"
                  placeholder="State Short Code"
                  value={st.short_code || ""}
                  onChange={(e) =>
                    handleStateChange(stateIdx, "short_code", e.target.value)
                  }
                />
                <div className="flex items-end gap-2 pb-1">
                  <YhButton
                    variant="secondary"
                    size="sm"
                    onClick={() => addCity(stateIdx)}
                  >
                    + City
                  </YhButton>
                  <button
                    type="button"
                    onClick={() => removeState(stateIdx)}
                    className="flex h-10 w-10 items-center justify-center rounded-lg text-[#DC2626] hover:bg-[#FEF2F2]"
                    aria-label="Remove state"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {(st.cities || []).map((city: any, cityIdx: number) => (
                <div
                  key={cityIdx}
                  className="mt-3 rounded-lg border border-[#EEF0F5] bg-white p-3"
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
                    <YhInput
                      label="City Name"
                      placeholder="City Name"
                      value={city.name || ""}
                      onChange={(e) =>
                        handleCityChange(
                          stateIdx,
                          cityIdx,
                          "name",
                          e.target.value
                        )
                      }
                    />
                    <div className="flex items-end gap-2 pb-1">
                      <YhButton
                        variant="secondary"
                        size="sm"
                        onClick={() => addLocation(stateIdx, cityIdx)}
                      >
                        + Zone
                      </YhButton>
                      <button
                        type="button"
                        onClick={() => removeCity(stateIdx, cityIdx)}
                        className="flex h-10 w-10 items-center justify-center rounded-lg text-[#DC2626] hover:bg-[#FEF2F2]"
                        aria-label="Remove city"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="mt-3">
                    <WebsiteToggle
                      checked={!!city.enabled}
                      onChange={(value) =>
                        handleCityChange(stateIdx, cityIdx, "enabled", value)
                      }
                      label="Show city on website header"
                    />
                  </div>

                  <div className="mt-3">
                    <label className="yh-label">City Icon (website header)</label>
                    <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-[#D1D5DB] bg-[#F9FAFB] px-4 py-4 text-center hover:border-[#A02448] hover:bg-[#F5F6FF]">
                      <span className="text-sm text-[#6B7280]">
                        {uploadingCityKey === `${stateIdx}-${cityIdx}`
                          ? "Uploading…"
                          : "Drag and drop or click to upload city logo"}
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        disabled={uploadingCityKey !== null}
                        onChange={(e) =>
                          handleCityFileChange(e, stateIdx, cityIdx)
                        }
                      />
                    </label>
                    {city.icon ? (
                      <div className="relative mt-3 inline-block h-16 w-16 overflow-hidden rounded-lg border border-[#EEF0F5]">
                        <img
                          src={city.icon}
                          alt={city.name || "City"}
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            handleCityChange(stateIdx, cityIdx, "icon", "")
                          }
                          className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <p className="mt-1 text-xs text-[#6B7280]">
                        No city icon yet — header shows city name only until you upload one.
                      </p>
                    )}
                  </div>

                  {(city.zones || []).map((loc: any, locIdx: number) => (
                    <div
                      key={locIdx}
                      className="mt-2 grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_auto_auto]"
                    >
                      <YhInput
                        label="Zone Name"
                        placeholder="Zone Name"
                        value={loc.name || ""}
                        onChange={(e) =>
                          handleZoneChange(
                            stateIdx,
                            cityIdx,
                            locIdx,
                            "name",
                            e.target.value
                          )
                        }
                      />
                      <label className="mb-2 flex items-center gap-2 text-sm text-[#374151]">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-[#D1D5DB]"
                          checked={!!loc.isTop}
                          onChange={(e) =>
                            handleZoneChange(
                              stateIdx,
                              cityIdx,
                              locIdx,
                              "isTop",
                              e.target.checked
                            )
                          }
                        />
                        Top
                      </label>
                      <button
                        type="button"
                        onClick={() =>
                          removeLocation(stateIdx, cityIdx, locIdx)
                        }
                        className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg text-[#DC2626] hover:bg-[#FEF2F2]"
                        aria-label="Remove zone"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-[#EEF0F5] bg-[#FAFBFC] px-5 py-4">
          <YhButton variant="secondary" size="sm" onClick={closeDrawer}>
            Cancel
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting || uploading || uploadingCityKey !== null}
          >
            {submitting ? "Saving…" : "Submit"}
          </YhButton>
        </div>
      </div>
    </div>
  );
};

export default ProjectLocationCreate;
